import React, { Ref, forwardRef } from 'react';
import {
  InputAttributes,
  NumericFormat,
  PatternFormat,
} from 'react-number-format';

export type InputFormatProps = {
  type: 'number' | 'tel' | 'numberRound' | 'number-only';
  name?: string;
  value?: any;
  unit?: string;
  readOnly?: boolean;
  disabled?: boolean;
  hasError?: boolean;
  styleWidthSize?: string;
  onChange?: (...event: any[]) => void;
} & InputAttributes;

/**
 * 원본은 base -> hasError -> readOnly 순으로 테두리를 덮어썼고
 * hasError 의 focus-within 에만 !important 가 붙어 readOnly 보다 우선했다.
 * 유틸리티는 전부 !important 라 출력 순서로 승자가 정해지므로 여기서 계산한다.
 */
function containerClass(
  hasError?: boolean,
  readOnly?: boolean,
  disabled?: boolean,
) {
  const border = hasError
    ? 'border-apps-coreProductError01 focus-within:border-apps-coreProductError01'
    : readOnly
      ? 'border-apps-coreProductBorder focus-within:border-apps-coreProductBorder'
      : 'border-apps-coreProductBorder focus-within:border-apps-corePrimary01';

  const surface =
    readOnly || disabled ? 'bg-apps-coreGray05' : 'bg-apps-coreGrayWhite';

  return `${border} ${surface} ${disabled ? 'text-apps-coreGray04' : ''}`;
}

/** admin.primary-5767DD — 원본의 :focus border-color 를 그대로 옮긴다 */
const FIELD =
  'w-full text-sm font-medium placeholder:text-apps-coreGray04 focus:border-admin-primary-5767DD';

const InputFormat = forwardRef<HTMLElement, InputFormatProps>(
  (
    {
      type,
      readOnly,
      disabled,
      hasError,
      styleWidthSize = 'auto',
      onChange,
      ...props
    },
    ref: Ref<HTMLElement>,
  ) => {
    /* 반올림 type='numberRound' 에서 blur 이벤트시 사용 */
    const numberRoundBlurHandler = (e: React.FocusEvent<HTMLInputElement>) => {
      const floatValue = e.target.value
        ? parseFloat(e.target.value.replace(/[$,]/g, ''))
        : 0;
      const roundedValue = Math.round(floatValue / 1000) * 1000;
      if (onChange) {
        onChange(roundedValue);
      }
      e.target.value = roundedValue.toLocaleString();
    };

    const fieldClass = `${FIELD} ${readOnly ? 'bg-apps-coreGray05' : ''} ${
      disabled ? 'text-apps-coreGray04' : ''
    }`;

    return (
      <label
        className={`relative flex h-12 items-center rounded-[5px] border border-solid px-5 py-2.5 [&_input]:w-full [&_input]:text-sm [&_input]:font-medium ${containerClass(
          hasError,
          readOnly,
          disabled,
        )}`}
        style={{ maxWidth: styleWidthSize }}
      >
        {type === 'number' && (
          <NumericFormat
            className={fieldClass}
            getInputRef={ref}
            thousandSeparator
            readOnly={readOnly}
            disabled={disabled}
            onValueChange={(v) => {
              onChange?.({
                target: {
                  value:
                    v.floatValue !== undefined ? String(v.floatValue) : null,
                },
              });
            }}
            {...props}
          />
        )}
        {type === 'numberRound' && (
          <NumericFormat
            className={fieldClass}
            getInputRef={ref}
            thousandSeparator
            readOnly={readOnly}
            disabled={disabled}
            onBlur={numberRoundBlurHandler}
            onValueChange={(v) => {
              onChange?.({
                target: {
                  value:
                    v.floatValue !== undefined ? String(v.floatValue) : null,
                },
              });
            }}
            {...props}
          />
        )}
        {type === 'tel' && (
          <PatternFormat
            className={fieldClass}
            getInputRef={ref}
            format="###-####-####"
            readOnly={readOnly}
            disabled={disabled}
            onValueChange={(v) => {
              onChange?.({
                target: { value: v.value },
              });
            }}
            {...props}
          />
        )}
        {type === 'number-only' && (
          <NumericFormat
            allowNegative={false}
            getInputRef={ref}
            readOnly={readOnly}
            disabled={disabled}
            onValueChange={(v) => {
              onChange?.({
                target: {
                  value: v.value !== undefined ? String(v.value) : null,
                },
              });
            }}
            {...props}
          />
        )}
      </label>
    );
  },
);

InputFormat.displayName = 'InputFormat';

export default InputFormat;
